// modules/ad-assets/useAssets.ts
// Hook для управління рекламними кабінетами, сторінками, постами та синхронізацією

import { useState, useCallback } from 'react';
import { api, ApiClientError } from '../../core/apiClient.ts';
import { useToast } from '../../core/contexts/ToastContext.tsx';
import type { AdAccount, Page, Post, PageGroup } from '../../core/types.ts';

// ─── SyncLog type (не включено у core/types.ts щоб не дублювати) ─

export interface SyncLog {
  id: number;
  socialAccountId: string | null;
  entity: string | null;
  status: 'ok' | 'error' | 'rate_limited';
  recordsSynced: number;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: string;
}

export interface SyncSummary {
  ok: boolean;
  adAccountsCount?: number;
  pixelsCount?: number;
  pagesCount?: number;
  postsCount?: number;
  errors?: string[];
  error?: string;
}

interface UseAssetsReturn {
  // Ad Accounts
  adAccounts: AdAccount[];
  adAccountsLoading: boolean;
  adAccountsError: string | null;
  fetchAdAccounts: (socialAccountId: string) => Promise<void>;
  refreshAdAccounts: (socialAccountId: string) => Promise<void>;
  adAccountsRefreshing: boolean;

  // Pages
  pages: Page[];
  pagesLoading: boolean;
  pagesError: string | null;
  fetchPages: (socialAccountId: string) => Promise<void>;
  refreshPages: (socialAccountId: string) => Promise<void>;
  pagesRefreshing: boolean;

  // Page Details
  pageDetails: Record<string, Page>; // cache by pageId
  pageDetailsLoading: Record<string, boolean>;
  fetchPageDetails: (pageId: string) => Promise<Page | null>;
  updatePageDetails: (pageId: string, fields: Record<string, string | null>) => Promise<Page | null>;
  syncPageDetails: (pageId: string) => Promise<Page | null>;

  // Posts
  posts: Post[];
  postsLoading: boolean;
  postsError: string | null;
  fetchPosts: (pageId: string) => Promise<void>;

  // Sync
  syncing: boolean;
  syncAccount: (socialAccountId: string) => Promise<SyncSummary>;

  // Sync Logs
  syncLogs: SyncLog[];
  syncLogsLoading: boolean;
  fetchSyncLogs: (socialAccountId: string) => Promise<void>;

  // Page Groups
  groups: PageGroup[];
  groupsLoading: boolean;
  fetchGroups: () => Promise<void>;
  createGroup: (name: string) => Promise<void>;
  deleteGroup: (id: number) => Promise<void>;
  assignGroup: (pageId: string, groupId: number | null) => Promise<void>;

  // Publishing
  publishPost: (pageId: string, message: string, photos: File[]) => Promise<{ id: string; post_id?: string }>;

  // Per-page post sync
  syncPagePosts: (pageId: string) => Promise<{ ok: boolean; postsCount?: number; error?: string }>;
  syncingPagePosts: Record<string, boolean>;
}

export function useAssets(): UseAssetsReturn {
  const { showToast } = useToast();

  // ── Ad Accounts ─────────────────────────────────────────────
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [adAccountsLoading, setAdAccountsLoading] = useState(false);
  const [adAccountsError, setAdAccountsError] = useState<string | null>(null);

  const fetchAdAccounts = useCallback(async (socialAccountId: string) => {
    setAdAccountsLoading(true);
    setAdAccountsError(null);
    try {
      const data = await api.get<AdAccount[]>(
        `/api/ad-accounts?social_account_id=${encodeURIComponent(socialAccountId)}`
      );
      setAdAccounts(data);
    } catch (err) {
      setAdAccountsError(
        err instanceof ApiClientError ? err.message : 'Не вдалося завантажити кабінети'
      );
    } finally {
      setAdAccountsLoading(false);
    }
  }, []);

  const [adAccountsRefreshing, setAdAccountsRefreshing] = useState(false);

  const refreshAdAccounts = useCallback(async (socialAccountId: string) => {
    setAdAccountsRefreshing(true);
    setAdAccountsError(null);
    try {
      await api.post<{ ok: boolean; count: number }>(
        `/api/ad-accounts/refresh/${encodeURIComponent(socialAccountId)}`
      );
      // Re-fetch from DB after sync
      const data = await api.get<AdAccount[]>(
        `/api/ad-accounts?social_account_id=${encodeURIComponent(socialAccountId)}`
      );
      setAdAccounts(data);
      showToast('Кабінети успішно оновлено', 'success');
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'Не вдалося оновити кабінети';
      setAdAccountsError(msg);
      showToast(msg, 'error');
    } finally {
      setAdAccountsRefreshing(false);
    }
  }, []);

  // ── Pages ───────────────────────────────────────────────────
  const [pages, setPages] = useState<Page[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState<string | null>(null);

  const fetchPages = useCallback(async (socialAccountId: string) => {
    setPagesLoading(true);
    setPagesError(null);
    try {
      const data = await api.get<Page[]>(
        `/api/pages?social_account_id=${encodeURIComponent(socialAccountId)}`
      );
      setPages(data);
    } catch (err) {
      setPagesError(
        err instanceof ApiClientError ? err.message : 'Не вдалося завантажити сторінки'
      );
    } finally {
      setPagesLoading(false);
    }
  }, []);

  const [pagesRefreshing, setPagesRefreshing] = useState(false);

  const refreshPages = useCallback(async (socialAccountId: string) => {
    setPagesRefreshing(true);
    setPagesError(null);
    try {
      await api.post<{ ok: boolean; count: number }>(
        `/api/pages/refresh/${encodeURIComponent(socialAccountId)}`
      );
      // Re-fetch from DB after sync
      const data = await api.get<Page[]>(
        `/api/pages?social_account_id=${encodeURIComponent(socialAccountId)}`
      );
      setPages(data);
      showToast('Fan Pages успішно оновлено', 'success');
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'Не вдалося оновити сторінки';
      setPagesError(msg);
      showToast(msg, 'error');
    } finally {
      setPagesRefreshing(false);
    }
  }, [showToast]);

  // ── Page Details ────────────────────────────────────────────
  const [pageDetails, setPageDetails] = useState<Record<string, Page>>({});
  const [pageDetailsLoading, setPageDetailsLoading] = useState<Record<string, boolean>>({});

  const fetchPageDetails = useCallback(async (pageId: string) => {
    setPageDetailsLoading((prev) => ({ ...prev, [pageId]: true }));
    try {
      const data = await api.get<Page>(`/api/pages/${encodeURIComponent(pageId)}/details`);
      setPageDetails((prev) => ({ ...prev, [pageId]: data }));
      return data;
    } catch (err) {
      console.error('Failed to fetch page details:', err);
      return null;
    } finally {
      setPageDetailsLoading((prev) => ({ ...prev, [pageId]: false }));
    }
  }, []);

  const updatePageDetails = useCallback(async (pageId: string, fields: Record<string, string | null>) => {
    setPageDetailsLoading((prev) => ({ ...prev, [pageId]: true }));
    try {
      const data = await api.patch<Page>(`/api/pages/${encodeURIComponent(pageId)}/details`, fields);
      setPageDetails((prev) => ({ ...prev, [pageId]: data }));
      
      // Також оновлюємо сторінку в загальному списку pages, якщо потрібно
      setPages((prevPages) => 
        prevPages.map((p) => (p.id === pageId ? { ...p, ...data } : p))
      );
      
      return data;
    } catch (err) {
      console.error('Failed to update page details:', err);
      throw err;
    } finally {
      setPageDetailsLoading((prev) => ({ ...prev, [pageId]: false }));
    }
  }, []);

  const syncPageDetails = useCallback(async (pageId: string) => {
    setPageDetailsLoading((prev) => ({ ...prev, [pageId]: true }));
    try {
      const data = await api.post<Page>(`/api/pages/${encodeURIComponent(pageId)}/details/sync`);
      setPageDetails((prev) => ({ ...prev, [pageId]: data }));
      
      setPages((prevPages) => 
        prevPages.map((p) => (p.id === pageId ? { ...p, ...data } : p))
      );

      return data;
    } catch (err) {
      console.error('Failed to sync page details:', err);
      throw err;
    } finally {
      setPageDetailsLoading((prev) => ({ ...prev, [pageId]: false }));
    }
  }, []);

  // ── Posts ───────────────────────────────────────────────────
  const [posts, setPosts] = useState<Post[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState<string | null>(null);

  const fetchPosts = useCallback(async (pageId: string) => {
    setPostsLoading(true);
    setPostsError(null);
    try {
      const data = await api.get<Post[]>(
        `/api/posts?page_id=${encodeURIComponent(pageId)}`
      );
      setPosts(data);
    } catch (err) {
      setPostsError(
        err instanceof ApiClientError ? err.message : 'Не вдалося завантажити пости'
      );
    } finally {
      setPostsLoading(false);
    }
  }, []);

  // ── Sync ────────────────────────────────────────────────────
  const [syncing, setSyncing] = useState(false);

  const syncAccount = useCallback(async (socialAccountId: string): Promise<SyncSummary> => {
    setSyncing(true);
    try {
      const result = await api.post<SyncSummary>(
        `/api/assets/sync/${encodeURIComponent(socialAccountId)}`
      );
      return result;
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'Помилка синхронізації';
      return { ok: false, error: msg };
    } finally {
      setSyncing(false);
    }
  }, []);

  // ── Sync Logs ───────────────────────────────────────────────
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [syncLogsLoading, setSyncLogsLoading] = useState(false);

  const fetchSyncLogs = useCallback(async (socialAccountId: string) => {
    setSyncLogsLoading(true);
    try {
      const data = await api.get<SyncLog[]>(
        `/api/sync-logs?social_account_id=${encodeURIComponent(socialAccountId)}&limit=10`
      );
      setSyncLogs(data);
    } catch {
      // Не критично — просто не показуємо логи
    } finally {
      setSyncLogsLoading(false);
    }
  }, []);

  // ── Page Groups ─────────────────────────────────────────────
  const [groups, setGroups] = useState<PageGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const data = await api.get<PageGroup[]>('/api/page-groups');
      setGroups(data);
    } catch (err) {
      console.error('Failed to fetch groups', err);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const createGroup = useCallback(async (name: string) => {
    await api.post('/api/page-groups', { name });
    await fetchGroups();
  }, [fetchGroups]);

  const deleteGroup = useCallback(async (id: number) => {
    await api.delete(`/api/page-groups/${id}`);
    await fetchGroups();
  }, [fetchGroups]);

  const assignGroup = useCallback(async (pageId: string, groupId: number | null) => {
    await api.put(`/api/pages/${encodeURIComponent(pageId)}/group`, { groupId });
    // Update local state
    setPages((prev) => prev.map((p) => p.id === pageId ? { ...p, groupId, groupName: groups.find(g => g.id === groupId)?.name } : p));
  }, [groups]);

  // ── Publishing ──────────────────────────────────────────────
  const publishPost = useCallback(async (pageId: string, message: string, photos: File[]) => {
    const formData = new FormData();
    if (message) formData.append('message', message);
    photos.forEach(photo => {
      formData.append('photos', photo);
    });
    return await api.post<{ id: string; post_id?: string }>(`/api/pages/${encodeURIComponent(pageId)}/publish`, formData);
  }, []);

  // ── Per-page post sync ──────────────────────────────────────
  const [syncingPagePosts, setSyncingPagePosts] = useState<Record<string, boolean>>({});

  const syncPagePosts = useCallback(async (pageId: string) => {
    setSyncingPagePosts((prev) => ({ ...prev, [pageId]: true }));
    try {
      const result = await api.post<{ ok: boolean; postsCount?: number; error?: string }>(
        `/api/pages/${encodeURIComponent(pageId)}/sync-posts`
      );
      return result;
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'Помилка синхронізації постів';
      return { ok: false, error: msg };
    } finally {
      setSyncingPagePosts((prev) => ({ ...prev, [pageId]: false }));
    }
  }, []);

  return {
    adAccounts, adAccountsLoading, adAccountsError, fetchAdAccounts, refreshAdAccounts, adAccountsRefreshing,
    pages, pagesLoading, pagesError, fetchPages, refreshPages, pagesRefreshing,
    pageDetails, pageDetailsLoading, fetchPageDetails, updatePageDetails, syncPageDetails,
    posts, postsLoading, postsError, fetchPosts,
    syncing, syncAccount,
    syncLogs, syncLogsLoading, fetchSyncLogs,
    groups, groupsLoading, fetchGroups, createGroup, deleteGroup, assignGroup,
    publishPost,
    syncPagePosts, syncingPagePosts,
  };
}
